<script setup>
import {computed, useSlots} from "vue";
import generateId from "@/composables/generateId";

const props = defineProps({
  modelValue: {
    type: [Boolean, Array],
    default: false
  },
  label: {
    type: String,
    default: ""
  },
  message: {
    type: String,
    default: ""
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  value: {
    type: [String, Number, Object],
    required: false,
    default: ""
  },
  /** Ключ для сравнения объектов в массиве **/
  compareKey: {
    type: String,
    default: "value"
  },
  /** Подсветка label для состояния checked **/
  highlightActiveLabel: {
    type: Boolean,
    default: false
  },
  /** Использовать ховер на input **/
  inputHover: {
    type: Boolean,
    default: true
  }
});

const slots = useSlots();
const emit = defineEmits(["update:modelValue", "change"]);

const localCheckedValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (!props.readonly) {
      emit("update:modelValue", value);
    }
  }
});

// Состояние чек-бокса.
const isChecked = computed(() => {
  if (Array.isArray(props.modelValue)) {
    // Если value — объект, сравниваем по указанному ключу
    if (typeof props.value === "object" && props.value !== null) {
      return props.modelValue.some(item =>
          typeof item === "object" &&
          item !== null &&
          item[props.compareKey] === props.value[props.compareKey]
      );
    }

    return props.modelValue.includes(props.value);
  }
  return props.modelValue;
});

const checkboxId = generateId();
</script>

<template>
  <label
    class="checkbox"
    :class="{
      'checkbox--readonly': props.readonly,
      'checkbox--input-hover': props.inputHover
    }"
    :for="checkboxId"
  >
    <span class="checkbox__wrapper">
      <span class="checkbox__box">
        <input
          :id="checkboxId"
          v-model="localCheckedValue"
          :value="props.value"
          class="checkbox__input"
          :disabled="disabled"
          :readonly="props.readonly"
          :class="{'checkbox__input--error': props.error}"
          type="checkbox"
          :aria-disabled="disabled ? 'true' : 'false'"
          @change="emit('change')"
        >
        <svg
          v-if="isChecked"
          class="checkbox__icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Фон  -->
          <path
            d="M13.833 0C15.0296 0 16 0.970375 16 2.16699V13.833C16 15.0296
            15.0296 16 13.833 16H2.16699C0.970375 16 0 15.0296 0 13.833V2.16699C0
            0.970375 0.970375 0 2.16699 0H13.833Z"
            fill="#1F9AFF"
          />
          <!-- Галочка -->
          <path
            d="M13.6094 4.40723C13.3023 4.09189 12.8101 4.06612 12.4727 4.33398L12.4072
            4.39062L6.67383 9.97168L4.10059 7.39941L4.03613 7.34082C3.70228 7.06861 3.21058
            7.08825 2.89941 7.39941C2.58825 7.71058 2.56861 8.20228 2.84082 8.53613L2.89941
            8.60059L6.06543 11.7676C6.39425 12.0964 6.92659 12.0998 7.25977 11.7754L13.5928
            5.60938L13.6523 5.54492C13.9288 5.21483 13.916 4.72248 13.6094 4.40723Z"
            fill="#FFFFFF"
          />
        </svg>
      </span>
      <span
        v-if="props.label || slots['default']"
        class="checkbox__label"
        :class="{'checkbox__label--active': localCheckedValue && props.highlightActiveLabel}"
      >
        <template v-if="props.label">
          {{ props.label }}
        </template>
        <template v-if="slots['default']">
          <slot />
        </template>
      </span>
    </span>
    <span
      v-if="props.message"
      class="checkbox__message"
    >
      {{ props.message }}
    </span>
  </label>
</template>

<style scoped lang="scss">
.checkbox {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;

  &--readonly {
    pointer-events: none;
    cursor: default;
  }

  &__wrapper {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  &__box {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &--input-hover:has(&__input:not(:disabled):not(:checked):not(&__input--error)) &__box:hover &__input {
    border: 1px solid var(--icon-icon-on);
  }

  &__icon {
    width: 16px;
    height: 16px;
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  &__label {
    color: var(--text-text-main);
    font-size: 14px;
    font-weight: 400;
    overflow-wrap: anywhere;
    line-height: 16px;
    cursor: pointer;

    &--active {
      color: var(--text-text-on);
    }
  }

  &__input {
    all: unset;
    box-sizing: border-box;
    min-width: 16px;
    min-height: 16px;
    width: 16px;
    height: 16px;
    border: 1px solid var(--icon-icon-secondary);
    background: var(--background-bg-white);
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;



    &:disabled {
      pointer-events: none;
      border-color: var(--icon-icon-disabled);
    }

    &--error {
      border-color: var(--icon-icon-error);
    }

    &:checked {
      border: 1px solid transparent;
      background: none;
    }
  }

  &__message {
    color: var(--text-text-error);
    font-size: 12px;
    font-weight: 400;
  }

  &__box:has(&__input:disabled) + &__label {
    color: var(--text-04);
  }

  &:has(&__input:disabled) &__message {
    color: var(--text-04);
  }
}
</style>